/**
 * CASE MOCK DATA
 *
 * Centralized mock data for cases including list views and records.
 * Uses mockDataManager for localStorage persistence.
 */

import { createPersistedMockData } from '../mockDataManager';
import type { ListView, ListViewRecord } from '../../types/listView';

/**
 * DEFAULT CASE LIST VIEWS
 */
export const defaultCaseListViews: ListView[] = [
  {
    id: 'lv_cases_all',
    name: 'All Cases',
    apiName: 'all_cases',
    objectApiName: 'Case',
    description: 'All case records',
    isDefault: true,
    columns: [
      { fieldApiName: 'id', fieldLabel: 'Case ID', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'caseNumber', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'contactName', fieldLabel: 'Contact', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'accountName', fieldLabel: 'Account', fieldType: 'text', isVisible: true, order: 6 },
      { fieldApiName: 'productName', fieldLabel: 'Product', fieldType: 'text', isVisible: true, order: 7 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 8 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_cases_open',
    name: 'Open Cases',
    apiName: 'open_cases',
    objectApiName: 'Case',
    description: 'Active cases requiring attention',
    isDefault: false,
    columns: [
      { fieldApiName: 'caseNumber', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'type', fieldLabel: 'Type', fieldType: 'text', isVisible: true, order: 4 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'date', isVisible: true, order: 6 },
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_cases_escalated',
    name: 'Escalated Cases',
    apiName: 'escalated_cases',
    objectApiName: 'Case',
    description: 'Cases that have been escalated',
    isDefault: false,
    columns: [
      { fieldApiName: 'caseNumber', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'accountName', fieldLabel: 'Account', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 4 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'date', isVisible: true, order: 5 },
      { fieldApiName: 'responseTime', fieldLabel: 'Response Time (hrs)', fieldType: 'number', isVisible: true, order: 6 },
    ],
    filters: [
      { fieldApiName: 'escalated', operator: 'equals', value: true }
    ],
    createdBy: 'Admin',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_cases_critical',
    name: 'Critical Priority',
    apiName: 'critical_priority',
    objectApiName: 'Case',
    description: 'Critical priority cases',
    isDefault: false,
    columns: [
      { fieldApiName: 'caseNumber', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'accountName', fieldLabel: 'Account', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 4 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'date', isVisible: true, order: 5 },
      { fieldApiName: 'responseTime', fieldLabel: 'Response Time (hrs)', fieldType: 'number', isVisible: true, order: 6 },
    ],
    filters: [
      { fieldApiName: 'priority', operator: 'equals', value: 'Critical' }
    ],
    sortBy: {
      fieldApiName: 'createdDate',
      direction: 'desc'
    },
    createdBy: 'Admin',
    createdDate: '2024-04-01T10:00:00Z'
  },
  {
    id: 'lv_cases_credit_block',
    name: 'Credit Block Cases',
    apiName: 'credit_block_cases',
    objectApiName: 'Case',
    description: 'Cases linked to Credit Block clients',
    isDefault: false,
    columns: [
      { fieldApiName: 'id', fieldLabel: 'Case ID', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'caseNumber', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'accountName', fieldLabel: 'Account', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'productName', fieldLabel: 'Product', fieldType: 'text', isVisible: true, order: 6 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 7 },
    ],
    createdBy: 'System',
    createdDate: '2024-06-01T00:00:00Z'
  },
  {
    id: 'lv_cases_by_product',
    name: 'By Product',
    apiName: 'cases_by_product',
    objectApiName: 'Case',
    description: 'Cases grouped by product',
    isDefault: false,
    columns: [
      { fieldApiName: 'caseNumber', fieldLabel: 'Case Number', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'subject', fieldLabel: 'Subject', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'productName', fieldLabel: 'Product', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'priority', fieldLabel: 'Priority', fieldType: 'badge', isVisible: true, order: 4 },
      { fieldApiName: 'accountName', fieldLabel: 'Account', fieldType: 'text', isVisible: true, order: 5 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 6 },
    ],
    sortBy: {
      fieldApiName: 'productName',
      direction: 'asc'
    },
    createdBy: 'Admin',
    createdDate: '2024-05-15T09:00:00Z'
  }
];

/**
 * DEFAULT CASE RECORDS
 */
export const defaultCaseRecords: ListViewRecord[] = [
  {
    id: 'case_001',
    caseNumber: 'C-00001',
    subject: 'Unable to access account after password reset',
    description: 'User cannot log in after requesting password reset',
    status: 'In Progress',
    priority: 'High',
    type: 'Problem',
    origin: 'Web',
    contactName: 'John Anderson',
    accountId: 'client_004',
    accountName: 'Tech Innovations Inc',
    productName: 'Credit Repair',
    owner: 'Sarah Johnson',
    createdDate: '2025-01-10T14:30:00Z',
    lastModifiedDate: '2025-01-16T11:30:00Z',
    responseTime: 2,
    escalated: false
  },
  {
    id: 'case_002',
    caseNumber: 'C-00002',
    subject: 'Software crash during data export',
    description: 'Application crashes when exporting large datasets',
    status: 'Escalated',
    priority: 'Critical',
    type: 'Bug Report',
    origin: 'Email',
    contactName: 'Sarah Martinez',
    accountId: 'client_003',
    accountName: 'Global Manufacturing Corp',
    productName: 'ID Theft Recovery',
    owner: 'Michael Chen',
    createdDate: '2025-01-12T08:15:00Z',
    lastModifiedDate: '2025-01-16T09:45:00Z',
    responseTime: 1,
    escalated: true
  },
  {
    id: 'case_003',
    caseNumber: 'C-00003',
    subject: 'Request for custom reporting feature',
    description: 'Need ability to create custom dashboard reports',
    status: 'New',
    priority: 'Medium',
    type: 'Feature Request',
    origin: 'Phone',
    contactName: 'Emily Davis',
    accountId: 'client_006',
    accountName: 'Retail Solutions LLC',
    productName: 'Credit Development',
    owner: 'Emily Davis',
    createdDate: '2025-01-16T10:00:00Z',
    lastModifiedDate: '2025-01-16T10:00:00Z',
    escalated: false
  },
  {
    id: 'case_004',
    caseNumber: 'C-00004',
    subject: 'How to configure multi-factor authentication',
    description: 'Need guidance on MFA setup for team',
    status: 'Waiting on Customer',
    priority: 'Low',
    type: 'Question',
    origin: 'Chat',
    contactId: 'contact_004',
    contactName: 'Michael Brown',
    accountId: 'client_005',
    accountName: 'Financial Services Group',
    productName: 'Credit Monitoring',
    owner: 'John Smith',
    createdDate: '2025-01-13T11:45:00Z',
    lastModifiedDate: '2025-01-14T16:30:00Z',
    responseTime: 4,
    escalated: false
  },
  {
    id: 'case_005',
    caseNumber: 'C-00005',
    subject: 'Integration with third-party CRM failing',
    description: 'API connection returns 401 errors',
    status: 'Waiting on Engineering',
    priority: 'High',
    type: 'Problem',
    origin: 'Email',
    contactName: 'Robert Taylor',
    accountId: 'client_007',
    accountName: 'Healthcare Innovations',
    productName: 'Credit Repair',
    owner: 'David Wilson',
    createdDate: '2025-01-09T13:00:00Z',
    lastModifiedDate: '2025-01-15T10:15:00Z',
    responseTime: 3,
    escalated: false
  },
  {
    id: 'case_006',
    caseNumber: 'C-00006',
    subject: 'Billing discrepancy on January invoice',
    description: 'Charged for services not used',
    status: 'Resolved',
    priority: 'Medium',
    type: 'Problem',
    origin: 'Phone',
    contactName: 'Jessica White',
    accountId: 'client_009',
    accountName: 'Construction Pro LLC',
    productName: 'Credit Development',
    owner: 'Sarah Johnson',
    createdDate: '2025-01-07T09:30:00Z',
    closedDate: '2025-01-09T15:00:00Z',
    lastModifiedDate: '2025-01-09T15:00:00Z',
    responseTime: 2,
    resolutionTime: 54,
    escalated: false
  },
  {
    id: 'case_007',
    caseNumber: 'C-00007',
    subject: 'Performance degradation during peak hours',
    description: 'System becomes unresponsive between 2-4 PM',
    status: 'Escalated',
    priority: 'Critical',
    type: 'Problem',
    origin: 'Web',
    contactName: 'Daniel Kim',
    accountId: 'client_008',
    accountName: 'Education Tech Solutions',
    productName: 'Credit Monitoring',
    owner: 'Michael Chen',
    createdDate: '2025-01-11T13:30:00Z',
    lastModifiedDate: '2025-01-16T10:00:00Z',
    responseTime: 1,
    escalated: true
  },
  {
    id: 'case_008',
    caseNumber: 'C-00008',
    subject: 'Mobile app not syncing with web version',
    description: 'Changes made on mobile do not appear on desktop',
    status: 'In Progress',
    priority: 'Medium',
    type: 'Bug Report',
    origin: 'Web',
    contactName: 'James Miller',
    accountId: 'client_001',
    accountName: 'Logistics Central',
    productName: 'Credit Monitoring',
    owner: 'John Smith',
    createdDate: '2025-01-14T09:00:00Z',
    lastModifiedDate: '2025-01-16T13:45:00Z',
    responseTime: 5,
    escalated: false
  },
  {
    id: 'case_009',
    caseNumber: 'C-00009',
    subject: 'Request for API documentation updates',
    description: 'Current API docs missing webhook examples',
    status: 'New',
    priority: 'Low',
    type: 'Feature Request',
    origin: 'Social Media',
    contactId: 'contact_003',
    contactName: 'Amanda Foster',
    accountId: 'client_002',
    accountName: 'Marketing Pros',
    productName: 'Credit Repair',
    owner: 'Sarah Johnson',
    createdDate: '2025-01-16T11:00:00Z',
    lastModifiedDate: '2025-01-16T11:00:00Z',
    escalated: false
  },
  {
    id: 'case_010',
    caseNumber: 'C-00010',
    subject: 'User permissions not saving correctly',
    description: 'Role changes revert after logout',
    status: 'Escalated',
    priority: 'High',
    type: 'Bug Report',
    origin: 'Phone',
    contactName: 'Christopher Lee',
    accountId: 'client_001',
    accountName: 'Software Dynamics',
    productName: 'ID Theft Recovery',
    owner: 'Michael Chen',
    createdDate: '2025-01-15T16:00:00Z',
    lastModifiedDate: '2025-01-16T15:30:00Z',
    responseTime: 2,
    escalated: true
  },
  {
    id: 'case_011',
    caseNumber: 'C-00011',
    subject: 'Training request for new team members',
    description: 'Need onboarding session for 5 new users',
    status: 'Closed',
    priority: 'Low',
    type: 'Question',
    origin: 'Email',
    contactName: 'Sarah Martinez',
    accountId: 'client_004',
    accountName: 'Tech Innovations Inc',
    productName: 'Credit Development',
    owner: 'Emily Davis',
    createdDate: '2025-01-08T10:00:00Z',
    closedDate: '2025-01-11T14:00:00Z',
    lastModifiedDate: '2025-01-11T14:00:00Z',
    responseTime: 6,
    resolutionTime: 76,
    escalated: false
  },
  {
    id: 'case_012',
    caseNumber: 'C-00012',
    subject: 'Email notifications not being received',
    description: 'Alerts and updates not arriving in inbox',
    status: 'Resolved',
    priority: 'Medium',
    type: 'Problem',
    origin: 'Chat',
    contactName: 'David Park',
    accountId: 'client_005',
    accountName: 'Financial Services Group',
    productName: 'Credit Monitoring',
    owner: 'John Smith',
    createdDate: '2025-01-13T14:30:00Z',
    closedDate: '2025-01-15T10:20:00Z',
    lastModifiedDate: '2025-01-15T10:20:00Z',
    responseTime: 3,
    resolutionTime: 44,
    escalated: false
  },
  {
    id: 'case_013',
    caseNumber: 'C-00013',
    subject: 'Data migration assistance needed',
    description: 'Help required to migrate from legacy system',
    status: 'In Progress',
    priority: 'High',
    type: 'Problem',
    origin: 'Phone',
    contactId: 'contact_001',
    contactName: 'Jennifer Taylor',
    accountId: 'client_007',
    accountName: 'Healthcare Innovations',
    productName: 'Credit Repair',
    owner: 'David Wilson',
    createdDate: '2025-01-09T09:00:00Z',
    lastModifiedDate: '2025-01-16T12:00:00Z',
    responseTime: 2,
    escalated: false
  },
  {
    id: 'case_014',
    caseNumber: 'C-00014',
    subject: 'Dashboard widgets not loading',
    description: 'Charts and graphs show loading spinner indefinitely',
    status: 'New',
    priority: 'Medium',
    type: 'Bug Report',
    origin: 'Web',
    contactName: 'Lisa Wong',
    accountId: 'client_008',
    accountName: 'Education Tech Solutions',
    productName: 'ID Theft Recovery',
    owner: 'Sarah Johnson',
    createdDate: '2025-01-16T14:00:00Z',
    lastModifiedDate: '2025-01-16T14:00:00Z',
    escalated: false
  },
  {
    id: 'case_015',
    caseNumber: 'C-00015',
    subject: 'Bulk import feature enhancement',
    description: 'Request to support CSV files larger than 100MB',
    productName: 'Credit Development',
    status: 'Waiting on Engineering',
    priority: 'Low',
    type: 'Feature Request',
    origin: 'Email',
    contactName: 'James Miller',
    accountId: 'client_001',
    accountName: 'Logistics Central',
    owner: 'Michael Chen',
    createdDate: '2025-01-12T11:30:00Z',
    lastModifiedDate: '2025-01-14T16:00:00Z',
    responseTime: 8,
    escalated: false
  },
  {
    id: 'case_016',
    caseNumber: 'C-00016',
    subject: 'Equifax bureau unlock request',
    description: 'Client requested temporary unblock for loan application',
    status: 'In Progress',
    priority: 'High',
    type: 'Credit Block',
    origin: 'Phone',
    contactName: 'Sarah Martinez',
    accountId: 'client_001',
    accountName: 'Logistics Central',
    productName: 'Credit Block',
    owner: 'Emily Davis',
    createdDate: '2025-01-15T09:00:00Z',
    lastModifiedDate: '2025-01-16T14:00:00Z',
    responseTime: 2,
    escalated: false
  },
  {
    id: 'case_017',
    caseNumber: 'C-00017',
    subject: 'Experian PIN not received',
    description: 'Client has not received PIN in mail after 2 weeks',
    status: 'New',
    priority: 'Medium',
    type: 'Credit Block',
    origin: 'Email',
    contactName: 'Christopher Lee',
    accountId: 'client_002',
    accountName: 'Software Dynamics',
    productName: 'Credit Block',
    owner: 'Michael Chen',
    createdDate: '2025-01-16T11:30:00Z',
    lastModifiedDate: '2025-01-16T11:30:00Z',
    escalated: false
  }
];

/**
 * PERSISTED STORAGE
 */
export const persistedCaseListViews = createPersistedMockData(
  'crm_case_list_views',
  defaultCaseListViews
);

export const persistedCaseRecords = createPersistedMockData(
  'crm_case_records',
  defaultCaseRecords
);

/**
 * BACKWARD COMPATIBILITY
 */
export const mockCaseListViews = defaultCaseListViews;
export const mockCaseRecords = defaultCaseRecords;
